import { abrirMaterial, baseDe, idDaVariante, MATERIAL_POR_ID } from '../look/materiais';
import { existe, t } from './idioma';

/**
 * Nome de coisa do jogo: item, magia, skill, profissão, cidade, inimigo.
 *
 * O catálogo passa a ser **id + número**; o nome sai do resource. Onde o
 * servidor ainda manda um rótulo pronto, ele entra como reserva — assim o
 * cliente traduz o que já tem chave sem esperar o servidor mudar.
 */
function nomeOuReserva(chave: string, reserva?: string): string {
  if (existe(chave)) return t(chave);
  return reserva ?? t(chave);
}

export const nomeDaMagia = (id: string) => nomeOuReserva(`magia.${id}`);
/** Nome do material: chave `material.<id>`; o nome próprio da tabela é a reserva. */
export function nomeDoMaterial(id: string): string {
  return nomeOuReserva(`material.${id}`, MATERIAL_POR_ID.get(id)?.nome ?? id);
}

const KIT_GM = 'gmkit:';

/** Ícone de uma linha do menu de GM. Kit usa o peitoral da família, no metal do kit. */
export function visualGm(id: string): { icone: string } {
  if (id.startsWith(KIT_GM)) {
    const corpo = id.slice(KIT_GM.length);
    const familia = baseDe(corpo);
    const metal = corpo.length > familia.length ? corpo.slice(familia.length + 1) : '';
    return { icone: idDaVariante(`${familia}-chest`, metal || 'iron') };
  }
  return { icone: id };
}

/** Rótulo da linha. Kit de placas sai como "Shadow Iron Plate"; o resto segue o item. */
export function nomeDaLinhaGm(id: string, reserva?: string): string {
  if (!id.startsWith(KIT_GM)) return nomeDoItem(id, reserva);
  const corpo = id.slice(KIT_GM.length);
  const familia = baseDe(corpo);
  if (familia === 'plate') {
    const metalId = corpo.length > familia.length ? corpo.slice(familia.length + 1) : 'iron';
    return t('gm.item.kit.plate', { metal: nomeDoMaterial(metalId) });
  }
  // Couro cravejado e osso por material: "Kit de couro cravejado · Couro Espinhoso".
  const materialId = corpo.length > familia.length ? corpo.slice(familia.length + 1) : '';
  return materialId
    ? t('gm.item.kit.comMaterial', { kit: t(`gm.item.kit.${familia}`), material: nomeDoMaterial(materialId) })
    : t(`gm.item.kit.${familia}`);
}

/**
 * Nome do item. A variante (`bow@oak`) sai como "Arco de Oak": o nome da base mais o material
 * (`item.feitoDe`). O padrão (Iron, madeira comum) fica só com o nome da base.
 */
export function nomeDoItem(defId: string, reserva?: string): string {
  const { base, material } = abrirMaterial(defId);
  if (base === defId || !material || material.padrao) return nomeOuReserva(`item.${defId}`, reserva);
  const peca = nomeOuReserva(`item.${base}`);
  return existe('item.feitoDe')
    ? t('item.feitoDe', { peca, material: nomeDoMaterial(material.id) })
    : (reserva ?? peca);
}
export const nomeDoGrupoDeItem = (grupo: string) => nomeOuReserva(`grupoItem.${grupo}`);
/**
 * O cliente usa id camelCase (`magicResist`) e o catálogo usa hífen
 * (`skill.magic-resist`), que é a forma dos ids do resto do jogo. Normalizar
 * aqui evita duas listas de skill que precisam concordar para sempre.
 */
const emHifen = (id: string) => id.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

export const nomeDaSkill = (id: string, reserva?: string) =>
  nomeOuReserva(`skill.${emHifen(id)}`, reserva);
export const nomeDaProfissao = (id: string, reserva?: string) => nomeOuReserva(`profissao.${id}`, reserva);
export const nomeDoCabelo = (id: string, reserva?: string) => nomeOuReserva(`cabelo.${id}`, reserva);
export const nomeDaBarba = (id: string, reserva?: string) => nomeOuReserva(`barba.${id}`, reserva);
export const nomeDoInimigo = (defId: string, reserva?: string) => nomeOuReserva(`inimigo.${defId}`, reserva);
export const nomeDoSlot = (slot: string) => nomeOuReserva(`slot.${slot}`);
export const nomeDoCargo = (id: string, reserva?: string) => nomeOuReserva(`cargo.${id}`, reserva);

/**
 * Nome de cidade é **nome próprio e não se traduz** (Britain é Britain nos três
 * idiomas). A descrição é frase, e essa traduz.
 */
export const descDaCidade = (id: string, reserva?: string) => nomeOuReserva(`cidade.${id}.desc`, reserva);
/** O mapa de caçada do Chefe do Mundo (`fire-dungeon`…): nome próprio, mas o Forte Orc traduz. */
export const nomeDoMapa = (id: string) => nomeOuReserva(`chefe.mapa.${id}`, id);

/** `inocente` / `criminoso` / `assassino` — hoje o fio manda a palavra em português. */
const NOTORIEDADE_POR_PALAVRA: Readonly<Record<string, string>> = {
  inocente: 'innocent',
  criminoso: 'criminal',
  assassino: 'murderer',
  innocent: 'innocent',
  criminal: 'criminal',
  murderer: 'murderer'
};

export function nomeDaNotoriedade(raw: string): string {
  const id = NOTORIEDADE_POR_PALAVRA[raw] ?? raw;
  return nomeOuReserva(`notoriedade.${id}`, raw);
}

const TITULO_PAPEL = /^npc\.titulo\.([a-z0-9-]+)$/;

/** `npc.titulo.mago` → `mago`. Título torto cai na fala comum. */
export function papelDoTitulo(titulo: string): string {
  return TITULO_PAPEL.exec(titulo)?.[1] ?? '';
}

/** Fala do NPC: tenta a voz do papel, cai na comum, sorteia a variante. */
export function falaDoNpc(papel: string, evento: string): string {
  const sorteia = (sufixo: string) => {
    let n = 0;
    while (existe(`npc.${sufixo}.${n + 1}`)) n++;
    return n === 0 ? '' : t(`npc.${sufixo}.${1 + Math.floor(Math.random() * n)}`);
  };
  return (papel && sorteia(`voz.${papel}.${evento}`)) || sorteia(`fala.${evento}`);
}

/** Guarda-roupa (techspec-guardaroupa): o servidor manda o nome em português; o catálogo traduz. */
export const nomeDaPecaDoTraje = (id: string, reserva?: string) => nomeOuReserva(`traje.peca.${id}`, reserva);
export const nomeDaCorDoTraje = (id: string, reserva?: string) => nomeOuReserva(`traje.cor.${id}`, reserva);
export const nomeDoLugarDoTraje = (id: string, reserva?: string) => nomeOuReserva(`traje.lugar.${id}`, reserva);
